import { useEffect, useRef } from "react";
import { Mail, MapPin, Facebook, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tyss-redesigned/artifacts/0k2pd0j0_rs%3Dw_800%2Ch_800%2Ccg_true%2Cm.webp";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Caregiver Boxes", href: "/caregiver-boxes" },
  { label: "Caregiver Resources", href: "/caregiver-resources" },
  { label: "Jay's Corner", href: "/jays-corner" },
  { label: "Partnerships", href: "/community-partnerships" },
  { label: "Contact", href: "/contact" },
];

export default function Footer() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) entry.target.querySelectorAll(".animate-on-scroll, .slide-left, .slide-right, .scale-in, .blur-in, .stagger-item, .line-animate").forEach((el) => el.classList.add("visible")); }),
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <footer ref={sectionRef} data-testid="footer-section" className="bg-[#1A3A4A] text-white pb-20">
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-10">
            {/* Logo & mission */}
            <div className="animate-on-scroll">
              <Link to="/" className="flex items-center gap-3 mb-4 no-underline">
                <img src={LOGO_URL} alt="TYSS Logo" className="w-12 h-12 rounded-lg object-contain" />
                <div>
                  <span className="font-serif font-bold text-xl text-white block leading-tight">TYSS</span>
                  <span className="text-[10px] font-sans tracking-wider uppercase text-white/50">Healing & Hope</span>
                </div>
              </Link>
              <p className="text-sm font-sans text-white/60 leading-relaxed">
                Supporting and empowering youth and their caregivers during mental health crises, fostering resilience and healing so families can thrive together.
              </p>
            </div>

            {/* Quick links */}
            <div className="animate-on-scroll delay-100">
              <p className="text-xs font-sans tracking-[0.15em] uppercase font-semibold text-white/40 mb-4">Quick Links</p>
              <div className="flex flex-col gap-2">
                {NAV_LINKS.map((link) => (
                  <Link key={link.href} to={link.href} data-testid={`footer-link-${link.label.toLowerCase().replace(/['\s]+/g, "-")}`}
                    className="text-sm font-sans text-white/60 hover:text-[#7CC8E0] transition-colors no-underline py-0.5">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact info */}
            <div className="animate-on-scroll delay-200">
              <p className="text-xs font-sans tracking-[0.15em] uppercase font-semibold text-white/40 mb-4">Contact</p>
              <div className="flex flex-col gap-3">
                <a href="mailto:info@tysservices.org" className="flex items-center gap-2 text-sm font-sans text-white/60 hover:text-white transition-colors no-underline">
                  <Mail className="w-4 h-4 text-[#7CC8E0]" /> info@tysservices.org
                </a>
                <div className="flex items-center gap-2 text-sm font-sans text-white/60">
                  <MapPin className="w-4 h-4 text-[#7CC8E0]" /> Massachusetts, US
                </div>
                <a href="https://www.facebook.com/profile.php?id=61579996282258" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook"
                  className="flex items-center gap-2 text-sm font-sans text-white/60 hover:text-white transition-colors no-underline">
                  <Facebook className="w-4 h-4 text-[#7CC8E0]" /> Facebook
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs font-sans text-white/40">&copy; {new Date().getFullYear()} Trauma Youth Support Services, Inc. All Rights Reserved.</p>
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/profile.php?id=61579996282258" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#0E8DB5]/30 transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4 text-white/70" />
              </a>
              <button data-testid="scroll-to-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#0E8DB5]/30 transition-colors border-none cursor-pointer" aria-label="Scroll to top">
                <ArrowUp className="w-4 h-4 text-white/70" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
